import React, { useMemo, useRef } from "react";
import { shallowEqual } from "react-redux";
import clsx from "clsx";
import ConfigureNavigation from "./ConfigureNavigation";
import Container from "../../../../shared-components/css-modules/lexus/atoms/Container";
import Engine from "./sections/engine/Engine";
import Colour from "./sections/colours/Colour";
import Wheels from "./sections/wheels/Wheels";
import Upholstery from "./sections/upholstery/Upholstery";
import PacksAndExtras from "./sections/packs-and-extras/PacksAndExtras";
import SpinContainer from "../common/spin/SpinContainer";
import SectionWrapper from "../common/section-wrapper/SectionWrapper";
import { CarConfigConfigureSection } from "../../../utils/types/SectionTypes";
import baseConfigStyles from "../Carconfig.module.scss";
import styles from "./Configure.module.scss";
import { useAphStepTracking } from "../../../hooks/useAphStepTracking";
import { useDebouncedEffect } from "../../../../../../common-deprecated/hooks";
import useSectionIntersectionObserver from "../../../hooks/useSectionIntersectionObserver";
import { useCarConfigSelector } from "../../../redux/store";
import Services from "./sections/services/Services";
import { useServicesSectionVisibility } from "../../../hooks/useSectionVisibility";
import { completeUnfinishedConfiguration, getLocalStorageParams } from "../../../utils/localStorage";
import { useComponentRegistry } from "../componentRegistry";
import Charging from "./sections/charging/Charging";
import { hasChargingSelector, hasPacksOrExtrasSelector } from "../../../utils/selectorUtils";

const Configure: React.FC = () => {
    const desktopContainerRef = useRef<HTMLDivElement | null>(null);

    const activeSection = useCarConfigSelector((state) => state.carConfigNavigation.activeSection);
    const localStorageParams = useCarConfigSelector(getLocalStorageParams);
    const showPacksAndExtras = useCarConfigSelector(hasPacksOrExtrasSelector, shallowEqual);
    const showCharging = useCarConfigSelector(hasChargingSelector, shallowEqual);

    const showServices = useServicesSectionVisibility();

    const sections = useMemo(
        () =>
            Object.values(CarConfigConfigureSection).filter((section) => {
                switch (section) {
                    case CarConfigConfigureSection.Services:
                        return showServices;
                    case CarConfigConfigureSection.Packs:
                        return showPacksAndExtras;
                    case CarConfigConfigureSection.Charging:
                        return showCharging;
                    case CarConfigConfigureSection.SummaryDetails:
                        return false;
                    default:
                        return true;
                }
            }),
        [showServices, showPacksAndExtras, showCharging],
    );

    // Creates a Mappable collection of all the sections and their Refs
    const sectionRefs = useMemo(
        () => new Map(sections.map((section) => [section, React.createRef<HTMLElement>()])),
        [sections],
    );

    const trackStep = useAphStepTracking();

    useSectionIntersectionObserver<CarConfigConfigureSection>(sectionRefs);
    useDebouncedEffect(
        () => {
            trackStep(activeSection);
            if (activeSection === CarConfigConfigureSection.Summary) {
                completeUnfinishedConfiguration(localStorageParams);
            }
        },
        500,
        [activeSection],
    );

    const Summary = useComponentRegistry("Summary");
    const ConfigCtas = useComponentRegistry("ConfigCtas");
    return useMemo(
        () => (
            <div id="tab-configure">
                <main
                    data-testid="configure-step"
                    className={clsx(styles.configure, styles.loaded)}
                    ref={desktopContainerRef}
                    id="carconfig-scroll-container"
                    data-activesection={activeSection}
                >
                    <Container className={styles["configure-grid"]}>
                        <SpinContainer />
                        <div className={styles.options}>
                            <Engine sectionRefs={sectionRefs} />
                            <Colour sectionRefs={sectionRefs} />
                            <Wheels sectionRefs={sectionRefs} />
                            <Upholstery sectionRefs={sectionRefs} />
                            {showPacksAndExtras && <PacksAndExtras sectionRefs={sectionRefs} />}
                            {showCharging && <Charging sectionRefs={sectionRefs} />}
                        </div>
                    </Container>
                    {showServices && <Services sectionRefs={sectionRefs} />}
                    <ConfigureNavigation
                        sections={sections}
                        activeSection={activeSection as CarConfigConfigureSection}
                    />
                    <SectionWrapper
                        sectionId={CarConfigConfigureSection.Summary}
                        sectionRef={sectionRefs.get(CarConfigConfigureSection.Summary)}
                        hasToggle
                    >
                        <Container>
                            <Summary />
                        </Container>
                    </SectionWrapper>
                </main>
                <div className={baseConfigStyles["config-ctas-desktop"]}>
                    <ConfigCtas />
                </div>
            </div>
        ),
        [ConfigCtas, Summary, activeSection, sectionRefs, sections, showCharging, showPacksAndExtras, showServices],
    );
};

export default Configure;
